import { useEffect, useMemo, useState } from "react";
import { Select, Radio, Spin, notification } from "antd";
import { Button, Icon } from "~/components";
import { saleApi } from "~/api";
import { handleRequest, apiError } from "~/utils";

function SaleCallSourceTransferForm({ selectedCount = 0, onSubmit }) {

	const [users, setUsers]         = useState([]);
	const [fromUsers, setFromUsers] = useState([]);
	const [loading, setLoading]     = useState(false);
	const [toUserId, setToUserId]   = useState(null);
	const [fromUserId, setFromUserId] = useState(null);
	const [mode, setMode]           = useState(selectedCount > 0 ? 'selected' : 'uncalled');
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			let [[errTo, resTo], [errFrom, resFrom]] = await Promise.all([
				handleRequest(saleApi.callSourceTransferUsers()),
				handleRequest(saleApi.callSourceTransferFromUsers()),
			]);
			if (!apiError('Tải danh sách nhân viên thất bại', errTo, resTo)) setUsers(resTo?.data || []);
			if (!apiError('Tải danh sách nhân viên bị chuyển nguồn thất bại', errFrom, resFrom)) setFromUsers(resFrom?.data || []);
			setLoading(false);
		};
		load();
	}, []);

	// Nếu mở form khi đã tick chọn -> mặc định chuyển mục đã chọn
	useEffect(() => {
		if (selectedCount > 0) setMode('selected');
	}, [selectedCount]);

	// Chỉ có 1 nhân viên bị chuyển nguồn -> chọn sẵn
	useEffect(() => {
		if (fromUsers.length === 1) setFromUserId(fromUsers[0].id);
	}, [fromUsers]);

	const fromOptions = useMemo(() => fromUsers.map(u => ({
		value: u.id,
		label: `${u.firstname || ''} ${u.lastname || ''}`.trim() + ` — ${u.uncalledCount || 0} nguồn chưa gọi`,
	})), [fromUsers]);

	// Người nhận không được trùng người bị chuyển
	const toOptions = useMemo(() => users
		.filter(u => u.id !== fromUserId)
		.map(u => ({
			value: u.id,
			label: `${u.firstname || ''} ${u.lastname || ''}`.trim() + (u.email ? ` (${u.email})` : ''),
		})), [users, fromUserId]);

	const selectedFrom = useMemo(() => fromUsers.find(u => u.id === fromUserId), [fromUsers, fromUserId]);

	const needFrom = mode !== 'selected';

	const handleSubmit = async () => {
		if (needFrom && !fromUserId) {
			notification.warning({ message: 'Thiếu thông tin', description: 'Vui lòng chọn nhân viên bị chuyển nguồn' });
			return;
		}
		if (!toUserId) {
			notification.warning({ message: 'Thiếu thông tin', description: 'Vui lòng chọn nhân viên nhận nguồn' });
			return;
		}
		if (mode === 'selected' && selectedCount === 0) {
			notification.warning({ message: 'Thiếu thông tin', description: 'Bạn chưa tick chọn liên hệ nào' });
			return;
		}
		setSubmitting(true);
		await onSubmit({ toUserId, fromUserId: needFrom ? fromUserId : null, mode });
		setSubmitting(false);
	};

	return (
		<div className="form">
			{needFrom && (
				<div className="form-group" style={{ marginBottom: 16 }}>
					<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>Nhân viên bị chuyển nguồn</label>
					{loading ? <Spin size="small" /> : (
						fromOptions.length > 0 ? (
							<Select
								showSearch
								value={fromUserId}
								onChange={setFromUserId}
								options={fromOptions}
								placeholder="Chọn nhân viên đang giữ nguồn chưa gọi"
								style={{ width: '100%' }}
								optionFilterProp="label"
							/>
						) : (
							<span style={{ color: '#888' }}>Không có nhân viên nào còn nguồn chưa gọi</span>
						)
					)}
				</div>
			)}

			<div className="form-group" style={{ marginBottom: 16 }}>
				<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>Nhân viên nhận nguồn</label>
				{loading ? <Spin size="small" /> : (
					<Select
						showSearch
						value={toUserId}
						onChange={setToUserId}
						options={toOptions}
						placeholder="Chọn nhân viên kinh doanh"
						style={{ width: '100%' }}
						optionFilterProp="label"
					/>
				)}
			</div>

			<div className="form-group" style={{ marginBottom: 20 }}>
				<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>Phạm vi chuyển</label>
				<Radio.Group value={mode} onChange={e => setMode(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					<Radio value="uncalled">
						Chỉ chuyển nguồn chưa gọi
						{selectedFrom && <span style={{ color: '#888' }}> ({selectedFrom.uncalledCount || 0})</span>}
					</Radio>
					<Radio value="all">Chuyển tất cả nguồn của nhân viên</Radio>
					<Radio value="selected" disabled={selectedCount === 0}>
						Chuyển các liên hệ đã chọn
						<span style={{ color: selectedCount > 0 ? '#1677ff' : '#bbb' }}> ({selectedCount})</span>
					</Radio>
				</Radio.Group>
			</div>

			<div className="d-flex justify-content-end gap">
				<Button
					primary
					leftIcon={submitting ? Icon.loading : Icon.switch}
					onClick={handleSubmit}
					disabled={submitting}
				>
					{submitting ? 'Đang chuyển...' : 'Chuyển nguồn'}
				</Button>
			</div>
		</div>
	);
}

export default SaleCallSourceTransferForm;
